import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, CheckCircle2, Clock3, CreditCard,
  FileCheck2, Files, GraduationCap, MapPin, MessageCircle,
  FileUp, ArrowUpRight, AlertCircle, LockKeyhole,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { useStudentPortal } from '../../contexts/StudentPortalContext';
import trackingService from '../../services/tracking.service';
import milestoneService from '../../services/milestone.service';
import studentPortalService from '../../services/studentPortal.service';
import type { DocumentChecklist, MilestoneListResponse } from '../../types';
import type { AdditionalPayment } from '../../types/studentPortal';
import CountryFlag from '../../components/CountryFlag';

const modeCopy = {
  en: {
    country_required: ['Choose your study destination', 'Select an available country and create your personal study plan.', '/app/onboarding'],
    setup_incomplete: ['Finish your application setup', 'Answer a few questions to see your exact package and payment schedule.', '/app/onboarding'],
    quote_ready: ['Your quotation is ready', 'Review the full service price and payment stages before accepting.', '/app/onboarding'],
    awaiting_deposit: ['Secure your application', 'Submit the initial payment to unlock documents and your consultant.', '/app/payments'],
    payment_review: ['Payment is being reviewed', 'We received your proof. You can follow its status here.', '/app/payments'],
    active: ['Continue your next step', '', '/app/application'],
    completed: ['Review your completed journey', 'Your application history and documents remain available.', '/app/application'],
  },
  ar: {
    country_required: ['اختر وجهتك الدراسية', 'اختر دولة متاحة وأنشئ خطتك الدراسية الشخصية.', '/app/onboarding'],
    setup_incomplete: ['أكمل إعداد طلبك', 'أجب عن أسئلة بسيطة لمعرفة الباقة وجدول الدفع بدقة.', '/app/onboarding'],
    quote_ready: ['عرض السعر جاهز', 'راجع سعر الخدمة ومراحل الدفع قبل الموافقة.', '/app/onboarding'],
    awaiting_deposit: ['ابدأ طلبك رسميًا', 'أرسل الدفعة الأولى لفتح المستندات والتواصل مع المستشار.', '/app/payments'],
    payment_review: ['جاري مراجعة الدفع', 'استلمنا إثبات الدفع ويمكنك متابعة حالته من هنا.', '/app/payments'],
    active: ['تابع خطوتك التالية', '', '/app/application'],
    completed: ['راجع رحلتك المكتملة', 'سيظل سجل الطلب والمستندات متاحًا لك.', '/app/application'],
  },
} as const;

export default function PortalHome() {
  const { workspace } = useStudentPortal();
  const { user } = useAuth();
  const { language, isRTL } = useI18n();

  const [checklist, setChecklist] = useState<DocumentChecklist | null>(null);
  const [milestones, setMilestones] = useState<MilestoneListResponse | null>(null);
  const [additional, setAdditional] = useState<AdditionalPayment[]>([]);

  const applicationId = workspace?.application?.id;

  useEffect(() => {
    if (!applicationId) return;
    let active = true;
    Promise.allSettled([
      trackingService.getChecklist(applicationId),
      milestoneService.getApplicationMilestones(applicationId),
      studentPortalService.getAdditionalPayments(),
    ]).then(([checkRes, mileRes, addRes]) => {
      if (!active) return;
      if (checkRes.status === 'fulfilled') setChecklist(checkRes.value);
      if (mileRes.status === 'fulfilled') setMilestones(mileRes.value);
      if (addRes.status === 'fulfilled') setAdditional(addRes.value);
    });
    return () => { active = false; };
  }, [applicationId]);

  if (!workspace) return null;

  const hasLegacyApplication = !!workspace.application && workspace.mode === 'country_required';
  const next = hasLegacyApplication
    ? (language === 'ar'
        ? ['تابع طلبك الحالي', 'تم استعادة طلبك القديم. يمكنك مراجعة بياناته ومستنداته دون تغيير وجهته.', '/app/application'] as const
        : ['Continue your existing application', 'Your legacy application was restored. Review its data and documents without changing its destination.', '/app/application'] as const)
    : modeCopy[language][workspace.mode];

  const docs = workspace.summaries.documents || {};
  const journey = workspace.summaries.journey || {};
  const universityTotal = workspace.summaries.universities?.total || 0;
  const paymentActions = workspace.summaries.additional_payments?.action_required || 0;
  const journeyPercent = journey.total ? Math.round(((journey.completed || 0) / journey.total) * 100) : 0;
  const Arrow = isRTL ? (props: LucideProps) => <ArrowRight {...props} className="rotate-180" /> : ArrowRight;
  const italySelected = !!workspace.country && ['IT', 'ITA'].includes(workspace.country.code.toUpperCase());
  const romaniaSelected = !!workspace.country && ['RO', 'ROU'].includes(workspace.country.code.toUpperCase());
  const canChangeCountry = !!workspace.country && (workspace.capabilities.change_country ?? (!workspace.application && !workspace.setup.contract_ready));
  const setupInProgress = ['country_required', 'setup_incomplete', 'quote_ready', 'awaiting_deposit', 'payment_review'].includes(workspace.mode);
  const setupSteps = [
    { ar: 'اختيار الدولة', en: 'Choose country', done: !!workspace.setup.country_selected },
    { ar: 'معرفة السعر والدفعات', en: 'Review price & payments', done: !!workspace.setup.quote_generated },
    { ar: 'مراجعة واعتماد العقد', en: 'Review & accept contract', done: !!workspace.setup.quote_accepted },
    { ar: 'إرسال الدفعة الأولى', en: 'Submit first payment', done: ['payment_review', 'active', 'completed'].includes(workspace.mode) },
  ];
  const currentSetupStep = setupSteps.findIndex((item) => !item.done);

  const modeLabel = {
    en: {
      country_required: 'Destination Required', setup_incomplete: 'Setup Incomplete', quote_ready: 'Quotation Ready',
      awaiting_deposit: 'Awaiting Deposit', payment_review: 'Payment Review', active: 'Active Application', completed: 'Completed',
    },
    ar: {
      country_required: 'اختيار الوجهة', setup_incomplete: 'الإعداد غير مكتمل', quote_ready: 'عرض السعر جاهز',
      awaiting_deposit: 'في انتظار الدفعة', payment_review: 'مراجعة الدفع', active: 'الطلب مفعل', completed: 'مكتمل',
    },
  }[language][workspace.mode];

  const totals = {
    total: milestones?.total_amount || 0,
    paid: milestones?.paid_amount || 0,
    remaining: milestones?.remaining_amount || 0,
    currency: milestones?.milestones[0]?.currency || 'EUR',
  };

  const docItems = checklist?.documents?.slice(0, 4).map((d) => ({
    id: d.id,
    name: language === 'ar' ? (d.document_name_ar || d.document_name) : d.document_name,
    status: d.status,
    required: d.is_mandatory,
  })) || [];

  const getDocStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="portal-badge success">{language === 'ar' ? 'معتمد ✓' : 'Approved ✓'}</span>;
      case 'rejected':
        return <span className="portal-badge danger">{language === 'ar' ? 'يحتاج تعديل' : 'Action needed'}</span>;
      case 'under_review':
      case 'pending_review':
      case 'uploaded':
      case 'submitted':
        return <span className="portal-badge warning">{language === 'ar' ? 'قيد الفحص' : 'In review'}</span>;
      default:
        return <span className="portal-badge neutral">{language === 'ar' ? 'مطلوب الرفع' : 'Pending upload'}</span>;
    }
  };

  return (
    <div className="portal-page">
      <div className="portal-page-heading">
        <div>
          <h1>
            {language === 'ar' ? `مرحبًا، ${user?.name || 'عزيزي الطالب'} 👋` : `Welcome, ${user?.name || 'Student'} 👋`}
          </h1>
        </div>
      </div>

      {setupInProgress && (
        <section className="portal-setup-guide" role="status" aria-label={language === 'ar' ? 'خطوات تفعيل الطلب' : 'Application activation steps'}>
          <div className="portal-setup-guide-head">
            <span><AlertTriangle size={21} /></span>
            <div><strong>{language === 'ar' ? 'أكمل إعداد طلبك لفتح جميع المزايا' : 'Complete setup to unlock your full workspace'}</strong></div>
          </div>
          <div className="portal-setup-steps">{setupSteps.map((item, index) => <div key={item.en} className={item.done ? 'done' : index === currentSetupStep ? 'current' : 'locked'}><span>{item.done ? <CheckCircle2 size={15} /> : index + 1}</span><b>{language === 'ar' ? item.ar : item.en}</b></div>)}</div>
          <div className="portal-setup-actions"><Link className="portal-button portal-button-primary" to={next[2]}>{next[0]}<Arrow size={17} /></Link>{canChangeCountry && <Link className="portal-button portal-button-outline" to="/app/onboarding?changeCountry=1"><MapPin size={16} />{language === 'ar' ? 'تغيير الدولة' : 'Change country'}</Link>}<small>{language === 'ar' ? 'تُثبت الدولة نهائيًا عند اعتماد العقد.' : 'Your destination becomes final when the contract is accepted.'}</small></div>
        </section>
      )}

      <section className="portal-grid portal-grid-4" aria-label="Application summary">
        <Link
          to="/app/application"
          className="portal-card portal-stat portal-stat-primary portal-stat-clickable"
          title={language === 'ar' ? 'عرض تفاصيل الطلب والمراحل' : 'View application details'}
        >
          <div className="portal-stat-top">
            <div>
              <p>{language === 'ar' ? 'حالة الطلب' : 'Application status'}</p>
              <strong style={{ fontSize: 20, marginTop: 6 }}>
                {workspace.application ? (language === 'ar' ? 'الطلب الحالي' : 'Active Plan') : (language === 'ar' ? 'الإعداد' : 'Setup')}
              </strong>
            </div>
            <span className="portal-stat-icon"><Clock3 size={20} /></span>
          </div>
          <div className="portal-stat-bottom">
            <span className="portal-stat-badge-light">
              {hasLegacyApplication ? workspace.application?.status.replaceAll('_', ' ') : modeLabel}
            </span>
          </div>
        </Link>

        <Link
          to="/app/documents"
          className="portal-card portal-stat portal-stat-clickable"
          title={language === 'ar' ? 'إدارة ورفع المستندات' : 'Manage & upload documents'}
        >
          <div className="portal-stat-top">
            <div>
              <strong>{docs.approved || 0}<small> / {docs.total || 0}</small></strong>
              <p>{language === 'ar' ? 'مستندات معتمدة' : 'Approved documents'}</p>
            </div>
            <span className="portal-stat-icon"><FileCheck2 size={20} /></span>
          </div>
          <div className="portal-stat-bottom">
            <div className="portal-progress">
              <span style={{ width: `${docs.total ? ((docs.approved || 0) / docs.total) * 100 : 0}%` }} />
            </div>
          </div>
        </Link>

        <Link
          to="/app/payments"
          className="portal-card portal-stat portal-stat-clickable"
          title={language === 'ar' ? 'عرض جدول الدفع والفواتير' : 'View fee schedule & payments'}
        >
          <div className="portal-stat-top">
            <div>
              <strong style={{ fontSize: paymentActions > 0 ? 24 : 22 }}>
                {paymentActions > 0 ? paymentActions : (language === 'ar' ? 'محدث' : 'Up to date')}
              </strong>
              <p>{language === 'ar' ? 'المدفوعات والمستحقات' : 'Payments & Fees'}</p>
            </div>
            <span className="portal-stat-icon"><CreditCard size={20} /></span>
          </div>
          <div className="portal-stat-bottom">
            <span className={`portal-badge ${paymentActions > 0 ? 'warning' : 'success'}`}>
              {paymentActions > 0 ? (language === 'ar' ? 'يتطلب إجراء' : 'Action required') : (language === 'ar' ? 'لا توجد متأخرات' : 'All clear')}
            </span>
          </div>
        </Link>

        <Link
          to="/app/universities"
          className="portal-card portal-stat portal-stat-clickable"
          title={language === 'ar' ? 'استعراض الجامعات والبرامج' : 'Explore universities & programs'}
        >
          <div className="portal-stat-top">
            <div>
              <strong>{universityTotal}</strong>
              <p>{language === 'ar' ? 'الجامعات والتخصصات' : 'Universities & Programs'}</p>
            </div>
            <span className="portal-stat-icon"><GraduationCap size={20} /></span>
          </div>
          <div className="portal-stat-bottom">
            <span className="portal-badge neutral">
              {journeyPercent}% {language === 'ar' ? 'اكتمال الخطة' : 'Plan progress'}
            </span>
          </div>
        </Link>
      </section>

      <section className="portal-grid-hero-chat" style={{ marginTop: 16 }}>
        <article className="portal-card portal-card-body portal-next-card">
          <div className="portal-next-visual">
            <span><MapPin size={24} /></span>
            <div className="portal-route-line" />
            <span className={`destination ${italySelected ? 'italy' : romaniaSelected ? 'romania' : ''}`}>
              {workspace.country ? <CountryFlag code={workspace.country.code} fallback={workspace.country.flag} label={workspace.country.name} /> : <GraduationCap size={24} />}
            </span>
          </div>
          <div className="portal-next-content">
            <span className="portal-badge" style={{ background: 'var(--portal-primary)', color: '#fff' }}>
              {language === 'ar' ? 'الخطوة التالية الموصى بها' : 'Recommended Next Step'}
            </span>
            <h2>{next[0]}</h2>
            {next[1] ? <p>{next[1]}</p> : null}
            <Link to={next[2]} className="portal-button portal-button-primary">
              {language === 'ar' ? 'متابعة الخطوة الآن' : 'Continue Now'}
              <Arrow size={17} />
            </Link>
          </div>
        </article>

        <article className="portal-card portal-card-body portal-contact-card">
          <div className="portal-contact-card-head">
            <span className="portal-contact-card-icon"><MessageCircle size={23} /></span>
            <div>
              <h3>{language === 'ar' ? 'تواصل مع المستشار' : 'Talk to your consultant'}</h3>
              <p>{language === 'ar' ? 'لديك سؤال عن طلبك؟ أرسل رسالتك مباشرة.' : 'Have a question about your application? Send a message directly.'}</p>
            </div>
          </div>
          <Link to="/app/chat" className="portal-button portal-button-primary portal-button-full portal-contact-card-action">
            <MessageCircle size={18} />
            <span>{language === 'ar' ? 'فتح المحادثة' : 'Open chat'}</span>
            <Arrow size={16} />
          </Link>
        </article>
      </section>

      <section className="portal-grid portal-grid-main" style={{ marginTop: 16 }}>
        <article className="portal-card portal-hub-card">
          <div className="portal-card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="portal-stat-icon" style={{ width: 36, height: 36 }}>
                <Files size={18} />
              </div>
              <h2>{language === 'ar' ? 'مستندات طلبك' : 'Application Documents'}</h2>
            </div>
            <Link to="/app/documents" className="portal-button portal-button-outline" style={{ minHeight: 36, padding: '0 12px', fontSize: 12 }}>
              {language === 'ar' ? 'عرض الكل' : 'View all'}
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="portal-card-body">
            <div style={{ display: 'grid', gap: 6, marginBottom: 16 }}>
              {docItems.map((doc, index) => (
                <div key={doc.id || index} className="portal-doc-item-row">
                  <div className="portal-doc-item-info">
                    <div className="portal-doc-item-icon">
                      <FileCheck2 size={16} />
                    </div>
                    <div>
                      <div className="portal-doc-item-name">{doc.name}</div>
                      <div className="portal-doc-item-sub">
                        {doc.required
                          ? (language === 'ar' ? 'مستند إلزامي' : 'Mandatory document')
                          : (language === 'ar' ? 'مستند داعم اختياري' : 'Optional supporting doc')}
                      </div>
                    </div>
                  </div>
                  <div>
                    {getDocStatusBadge(doc.status)}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ paddingTop: 8, borderTop: '1px solid var(--portal-line)' }}>
              <Link to="/app/documents" className="portal-button portal-button-primary portal-button-full">
                <FileUp size={16} />
                {language === 'ar' ? 'فتح وإدارة المستندات والرفع' : 'Manage & Upload Documents'}
              </Link>
            </div>
          </div>
        </article>

        <article className="portal-card portal-hub-card">
          <div className="portal-card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="portal-stat-icon" style={{ width: 36, height: 36 }}>
                <CreditCard size={18} />
              </div>
              <h2>{language === 'ar' ? 'المدفوعات والمستحقات' : 'Payments & Fees'}</h2>
            </div>
            <Link to="/app/payments" className="portal-button portal-button-outline" style={{ minHeight: 36, padding: '0 12px', fontSize: 12 }}>
              {language === 'ar' ? 'التفاصيل' : 'Details'}
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="portal-card-body">
            <div className="portal-payment-summary-box">
              <div>
                <small>{language === 'ar' ? 'إجمالي الخدمة' : 'Total Service'}</small>
                <strong>{Number(totals.total).toLocaleString()} <span style={{ fontSize: 11 }}>{totals.currency}</span></strong>
              </div>
              <div>
                <small>{language === 'ar' ? 'تم تأكيده' : 'Paid'}</small>
                <strong style={{ color: 'var(--portal-mint-dark)' }}>{Number(totals.paid).toLocaleString()} <span style={{ fontSize: 11 }}>{totals.currency}</span></strong>
              </div>
              <div>
                <small>{language === 'ar' ? 'المتبقي' : 'Remaining'}</small>
                <strong style={{ color: totals.remaining > 0 ? 'var(--portal-warning-dark)' : 'inherit' }}>
                  {Number(totals.remaining).toLocaleString()} <span style={{ fontSize: 11 }}>{totals.currency}</span>
                </strong>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              {milestones?.milestones && milestones.milestones.length > 0 ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {milestones.milestones.slice(0, 2).map((ms) => (
                    <div key={ms.id} className="portal-mini-row" style={{ padding: '4px 0' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {ms.status === 'confirmed' ? (
                          <CheckCircle2 size={16} color="var(--portal-mint)" />
                        ) : ms.status === 'locked' ? (
                          <LockKeyhole size={16} color="var(--portal-muted)" />
                        ) : (
                          <AlertCircle size={16} color="var(--portal-warning)" />
                        )}
                        <span style={{ fontSize: 12, fontWeight: 700 }}>
                          {language === 'ar' ? (ms.title_ar || ms.title) : ms.title}
                        </span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 800 }}>
                          {Number(ms.amount).toLocaleString()} {ms.currency}
                        </span>
                        <span className={`portal-badge ${ms.status === 'confirmed' ? 'success' : ms.status === 'locked' ? 'neutral' : 'warning'}`} style={{ minHeight: 22, fontSize: 9 }}>
                          {ms.status.replaceAll('_', ' ')}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              {additional.some((item) => item.status === 'pending_student') && (
                <div className="portal-alert" style={{ margin: '12px 0 0', padding: '10px 14px' }}>
                  <AlertCircle size={17} />
                  <div>
                    <strong style={{ fontSize: 12 }}>{language === 'ar' ? 'توجد رسوم إضافية بانتظار إثبات الدفع' : 'Additional fee awaiting proof'}</strong>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--portal-line)' }}>
              <Link to="/app/payments" className="portal-button portal-button-primary portal-button-full">
                <CreditCard size={16} />
                {language === 'ar' ? 'متابعة جدول الدفع وإرسال الإيصالات' : 'Go to Payments & Invoices'}
              </Link>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
}
